import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { memo, useState } from 'react';
import { useCertificatesDictionary } from '../../../hooks/queries/useCertificatesDictionary';
import { useCertificationFamiliesDictionary } from '../../../hooks/queries/useCertificationFamiliesDictionary';
import { useLanguageDictionary, useLanguageProficiencies } from '../../../hooks/queries/useLanguageDictionary';
import { useTechnologyCanonicalsDictionary } from '../../../hooks/queries/useTechnologyCanonicalsDictionary';
import { usePositionProfilesWithTechnologies } from '../../position-profiles';

export interface TechRow {
  technologyCanonicalId: number;
  canonicalName: string;
  requiredLevel: number | null;
  requiredWeight: number;
}

export interface CertRow {
  mode: 'cert' | 'family';
  certificateId: number | null;
  certificateTitle: string | null;
  certificationFamilyId: number | null;
  certificationFamilyName: string | null;
}

export interface LangRow {
  languageId: number;
  languageNativeName: string;
  requiredProficiencyId: number | null;
  requiredProficiencyName: string | null;
  requiredWeight: number;
}

interface Props {
  technologies: TechRow[];
  onTechnologiesChange: (v: TechRow[]) => void;
  certifications: CertRow[];
  onCertificationsChange: (v: CertRow[]) => void;
  languages: LangRow[];
  onLanguagesChange: (v: LangRow[]) => void;
}

const LEVEL_OPTIONS = [
  { value: '', label: 'Any (no minimum level)' },
  { value: '1', label: '1 - Foundation' },
  { value: '2', label: '2 - Basic working' },
  { value: '3', label: '3 - Independent' },
  { value: '4', label: '4 - Advanced' },
  { value: '5', label: '5 - Expert' },
];

const CERT_MODE_OPTIONS = [
  { value: 'cert', label: 'Specific cert' },
  { value: 'family', label: 'Any in family' },
];

export const SkillRequirementSelector = memo(function SkillRequirementSelector({
  technologies,
  onTechnologiesChange,
  certifications,
  onCertificationsChange,
  languages,
  onLanguagesChange,
}: Props) {
  const { data: canonicalsDict, isLoading: canonicalsLoading } = useTechnologyCanonicalsDictionary();
  const { data: certDict, isLoading: certsLoading } = useCertificatesDictionary();
  const { data: familiesDict, isLoading: familiesLoading } = useCertificationFamiliesDictionary();
  const { data: langDict } = useLanguageDictionary();
  const { data: proficiencies } = useLanguageProficiencies();
  const { data: profilesWithTech, isLoading: profilesLoading } = usePositionProfilesWithTechnologies();
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  const techOptions = (() => {
    if (!canonicalsDict) return [];
    const groups: Record<string, { value: string; label: string }[]> = {};
    for (const c of canonicalsDict) {
      const cat = c.category ?? 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ value: c.id.toString(), label: c.name });
    }
    return Object.entries(groups).map(([group, items]) => ({ group, items }));
  })();
  const certOptions = certDict?.map((c) => ({ value: c.id.toString(), label: c.title })) ?? [];
  const familyOptions = familiesDict?.map((f) => ({ value: f.id.toString(), label: f.name })) ?? [];
  const langOptions = langDict?.map((l) => ({ value: l.id.toString(), label: l.nativeName })) ?? [];
  const profOptions = [
    { value: '', label: 'Any' },
    ...(proficiencies?.map((p) => ({ value: p.id.toString(), label: p.level })) ?? []),
  ];

  const addTech = () =>
    onTechnologiesChange([...technologies, { technologyCanonicalId: 0, canonicalName: '', requiredLevel: null, requiredWeight: 50 }]);

  const profileOptions = (profilesWithTech ?? [])
    .filter((p) => p.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => ({ value: String(p.id), label: p.name }));

  const handleApplyProfile = () => {
    if (!selectedProfileId || !profilesWithTech) {
      return;
    }
    const profile = profilesWithTech.find((p) => p.id === Number(selectedProfileId));
    if (!profile) {
      return;
    }
    const existingIds = new Set(technologies.map((t) => t.technologyCanonicalId));
    const newRows: TechRow[] = profile.technologyCanonicals
      .filter((tc) => !existingIds.has(tc.technologyCanonicalId))
      .map((tc) => ({
        technologyCanonicalId: tc.technologyCanonicalId,
        canonicalName: tc.canonicalName,
        requiredLevel: tc.requiredLevel ?? null,
        requiredWeight: 50,
      }));
    onTechnologiesChange([...technologies, ...newRows]);
    setSelectedProfileId(null);
  };

  const updateTech = (idx: number, patch: Partial<TechRow>) =>
    onTechnologiesChange(technologies.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeTech = (idx: number) =>
    onTechnologiesChange(technologies.filter((_, i) => i !== idx));

  const addCert = () =>
    onCertificationsChange([...certifications, { mode: 'cert', certificateId: null, certificateTitle: null, certificationFamilyId: null, certificationFamilyName: null }]);

  const updateCert = (idx: number, patch: Partial<CertRow>) =>
    onCertificationsChange(certifications.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const switchCertMode = (idx: number, mode: 'cert' | 'family') =>
    onCertificationsChange(certifications.map((r, i) =>
      i === idx
        ? { mode, certificateId: null, certificateTitle: null, certificationFamilyId: null, certificationFamilyName: null }
        : r
    ));

  const removeCert = (idx: number) =>
    onCertificationsChange(certifications.filter((_, i) => i !== idx));

  const addLang = () =>
    onLanguagesChange([...languages, { languageId: 0, languageNativeName: '', requiredProficiencyId: null, requiredProficiencyName: null, requiredWeight: 50 }]);

  const updateLang = (idx: number, patch: Partial<LangRow>) =>
    onLanguagesChange(languages.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeLang = (idx: number) =>
    onLanguagesChange(languages.filter((_, i) => i !== idx));

  return (
    <Stack gap="xl">
      {/* Technologies */}
      <Box>
        <Stack gap={0} mb="sm">
          <Text fw={500} size="sm">Technologies</Text>
          <Text size="xs" c="dimmed">Level: Any, 1 Foundation, 2 Basic, 3 Independent, 4 Advanced, 5 Expert</Text>
          <Text size="xs" c="dimmed">Weight: 1–100% (higher = more important for fit score)</Text>
        </Stack>
        {/* Position profile pre-fill */}
        <Group gap="xs" mb="sm">
          <Select
            style={{ flex: 1 }}
            placeholder="Apply position profile…"
            data={profileOptions}
            value={selectedProfileId}
            onChange={setSelectedProfileId}
            searchable
            clearable
            disabled={!profilesLoading && profileOptions.length === 0}
            nothingFoundMessage="No role profiles defined yet"
            comboboxProps={{ shadow: 'xl', withinPortal: true }}
            aria-label="Position profile"
          />
          <Tooltip label="Add technologies from this profile to the list below. Existing technologies are not changed.">
            <Button
              variant="default"
              size="sm"
              disabled={!selectedProfileId}
              onClick={handleApplyProfile}
            >
              Apply
            </Button>
          </Tooltip>
        </Group>
        <Stack gap="xs">
          {technologies.map((row, idx) => (
            <Group key={idx} gap="xs" align="flex-end" wrap="nowrap">
              <Select
                placeholder="Technology"
                searchable
                limit={50}
                disabled={canonicalsLoading}
                data={techOptions}
                value={row.technologyCanonicalId ? row.technologyCanonicalId.toString() : null}
                onChange={(v) => {
                  const found = canonicalsDict?.find((c) => c.id.toString() === v);
                  updateTech(idx, { technologyCanonicalId: found?.id ?? 0, canonicalName: found?.name ?? '' });
                }}
                style={{ flex: 3, minWidth: 0 }}
                comboboxProps={{ shadow: 'xl', withinPortal: true }}
                aria-label="Technology"
              />
              <Select
                placeholder="Level"
                data={LEVEL_OPTIONS}
                value={row.requiredLevel != null ? row.requiredLevel.toString() : ''}
                onChange={(v) => updateTech(idx, { requiredLevel: v ? parseInt(v) : null })}
                style={{ flex: 1, minWidth: 80 }}
                comboboxProps={{ shadow: 'xl', withinPortal: true }}
                aria-label="Required level"
              />
              <Tooltip label="1–100% weight towards fit score (higher = more important)" withArrow>
                <NumberInput
                  placeholder="Weight %"
                  min={1}
                  max={100}
                  value={row.requiredWeight}
                  onChange={(v) => updateTech(idx, { requiredWeight: typeof v === 'number' ? v : 50 })}
                  style={{ flex: 1, minWidth: 80 }}
                  aria-label="Required weight"
                />
              </Tooltip>
              <ActionIcon variant="subtle" color="red" size="md" onClick={() => removeTech(idx)} aria-label="Remove technology">
                <XMarkIcon style={{ width: 18, height: 18 }} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="light"
            size="xs"
            leftSection={<PlusIcon style={{ width: 14, height: 14 }} />}
            onClick={addTech}
            mt={technologies.length > 0 ? 4 : 0}
            style={{ alignSelf: 'flex-start' }}
          >
            Add technology
          </Button>
        </Stack>
      </Box>

      <Divider />

      {/* Certifications */}
      <Box>
        <Stack gap={0} mb="sm">
          <Text fw={500} size="sm">Certifications</Text>
          <Text size="xs" c="dimmed">Candidate needs ANY ONE of these</Text>
        </Stack>
        <Stack gap="xs">
          {certifications.map((row, idx) => (
            <Group key={idx} gap="xs" align="flex-start" wrap="nowrap">
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <SegmentedControl
                  size="xs"
                  data={CERT_MODE_OPTIONS}
                  value={row.mode}
                  onChange={(v) => switchCertMode(idx, v as 'cert' | 'family')}
                />
                {row.mode === 'cert' ? (
                  <Select
                    placeholder="Certificate"
                    searchable
                    disabled={certsLoading}
                    data={certOptions}
                    value={row.certificateId != null ? row.certificateId.toString() : null}
                    onChange={(v) => {
                      const found = certDict?.find((c) => c.id.toString() === v);
                      updateCert(idx, { certificateId: found?.id ?? null, certificateTitle: found?.title ?? null });
                    }}
                    comboboxProps={{ shadow: 'xl', withinPortal: true }}
                    aria-label="Certificate"
                  />
                ) : (
                  <Select
                    placeholder="Certification family"
                    searchable
                    disabled={familiesLoading}
                    data={familyOptions}
                    value={row.certificationFamilyId != null ? row.certificationFamilyId.toString() : null}
                    onChange={(v) => {
                      const found = familiesDict?.find((f) => f.id.toString() === v);
                      updateCert(idx, { certificationFamilyId: found?.id ?? null, certificationFamilyName: found?.name ?? null });
                    }}
                    comboboxProps={{ shadow: 'xl', withinPortal: true }}
                    aria-label="Certification family"
                  />
                )}
              </Stack>
              <ActionIcon variant="subtle" color="red" size="md" mt={28} onClick={() => removeCert(idx)} aria-label="Remove certification">
                <XMarkIcon style={{ width: 18, height: 18 }} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="light"
            size="xs"
            leftSection={<PlusIcon style={{ width: 14, height: 14 }} />}
            onClick={addCert}
            mt={certifications.length > 0 ? 4 : 0}
            style={{ alignSelf: 'flex-start' }}
          >
            Add certification
          </Button>
        </Stack>
      </Box>

      <Divider />

      {/* Languages */}
      <Box>
        <Stack gap={0} mb="sm">
          <Text fw={500} size="sm">Languages</Text>
          <Text size="xs" c="dimmed">Weight: 1–100% (higher = more important for fit score)</Text>
        </Stack>
        <Stack gap="xs">
          {languages.map((row, idx) => (
            <Group key={idx} gap="xs" align="flex-end" wrap="nowrap">
              <Select
                placeholder="Language"
                searchable
                data={langOptions}
                value={row.languageId ? row.languageId.toString() : null}
                onChange={(v) => {
                  const found = langDict?.find((l) => l.id.toString() === v);
                  updateLang(idx, { languageId: found?.id ?? 0, languageNativeName: found?.nativeName ?? '' });
                }}
                style={{ flex: 2, minWidth: 0 }}
                comboboxProps={{ shadow: 'xl', withinPortal: true }}
                aria-label="Language"
              />
              <Select
                placeholder="Min. Proficiency"
                data={profOptions}
                value={row.requiredProficiencyId != null ? row.requiredProficiencyId.toString() : ''}
                onChange={(v) => {
                  const found = proficiencies?.find((p) => p.id.toString() === v);
                  updateLang(idx, {
                    requiredProficiencyId: found?.id ?? null,
                    requiredProficiencyName: found?.level ?? null,
                  });
                }}
                style={{ flex: 1, minWidth: 100 }}
                comboboxProps={{ shadow: 'xl', withinPortal: true }}
                aria-label="Minimum proficiency"
              />
              <Tooltip label="1–100% weight towards fit score (higher = more important)" withArrow>
                <NumberInput
                  placeholder="Weight %"
                  min={1}
                  max={100}
                  value={row.requiredWeight}
                  onChange={(v) => updateLang(idx, { requiredWeight: typeof v === 'number' ? v : 50 })}
                  style={{ flex: 1, minWidth: 80 }}
                  aria-label="Required weight"
                />
              </Tooltip>
              <ActionIcon variant="subtle" color="red" size="md" onClick={() => removeLang(idx)} aria-label="Remove language">
                <XMarkIcon style={{ width: 18, height: 18 }} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="light"
            size="xs"
            leftSection={<PlusIcon style={{ width: 14, height: 14 }} />}
            onClick={addLang}
            mt={languages.length > 0 ? 4 : 0}
            style={{ alignSelf: 'flex-start' }}
          >
            Add language
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
});
